import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken, logout, refreshToken } from '../services/AuthService';

const CHECK_INTERVAL_MS = 60 * 1000;   // check every 60 s
const REFRESH_THRESHOLD_MS = 30 * 60 * 1000; // refresh when < 30 min left

/**
 * useTokenRefresh
 * Silently refreshes the JWT when < 30 minutes remain on the token.
 * If the refresh fails (server down, token already expired), forces logout.
 * Safe to call in any protected page — no UI output.
 */
export function useTokenRefresh() {
  const navigate = useNavigate();
  const refreshingRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      const token = getToken();
      if (!token) return;

      let exp;
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        exp = payload.exp * 1000;
      } catch {
        return;
      }

      const msLeft = exp - Date.now();

      // Already expired — force logout
      if (msLeft <= 0) {
        logout();
        navigate('/', { replace: true });
        return;
      }

      // Close to expiry — refresh silently
      if (msLeft < REFRESH_THRESHOLD_MS && !refreshingRef.current) {
        refreshingRef.current = true;
        const ok = await refreshToken();
        refreshingRef.current = false;

        if (!ok) {
          // Refresh failed — log out cleanly
          logout();
          navigate('/', { replace: true });
        } else {
          console.log('[TokenRefresh] Token refreshed silently');
        }
      }
    };

    // Run immediately on mount, then on interval
    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [navigate]);
}
